import { Schema } from "effect"
import { UsageProvider } from "./common"
import { NonNegInt, ProfileId, RunnerId, UserId } from "./ids"

// #region usage-v2
/**
 * Wave F (decision 0062): usage and costs. Every key these shapes add to an existing struct is optional, so stored
 * events and records decode unchanged, and an older cloud's decoder (Effect's default `onExcessProperty: "ignore"`)
 * drops what it does not know.
 */

/** How one usage row is paid. Detected per run from the CLI, never assumed from a profile name. */
export const BillingClass = Schema.Literals([
  "subscription", // included in a person's plan (Claude Pro/Max/Team, ChatGPT Plus/Pro/Business …)
  "subscription_extra", // a plan's extra usage or purchased credits past its limits (Claude overage, Codex credits)
  "api_key", // metered per token on an API key (a person's, the workspace's, or Minga's own)
  "vendor_credits", // a cloud vendor's own unit (ACUs, AI Credits); later, with cloud runners
  "compute", // sandbox compute; only on ComputeUsage rows (never on a token row); later, with platform runners
  "unknown", // the runner did not say (runners before this wave), or the CLI's auth is unknown
])
export type BillingClass = typeof BillingClass.Type

/** Where the money figures of a row come from. */
export const CostSource = Schema.Literals([
  "cli_reported", // the CLI's own estimate (Claude modelUsage[m].costUSD; opencode/pi cost later)
  "price_table", // core/pricing.ts × tokens
  "vendor", // a vendor's billing figure (later)
  // A rougher guess: extra usage of unknown share, tokens rebuilt from per-step sums, a Codex row split at a model
  // reroute. The only value a runner may set (core decide strips the others).
  "estimate",
])
export type CostSource = typeof CostSource.Type

/** What the CLI reported as cost, as a per-run delta. `basis` = Claude's `costBasis` ('list' | 'managed' | 'unknown'). */
export const ReportedCost = Schema.Struct({
  microUsd: NonNegInt,
  basis: Schema.Literals(["list", "managed", "unknown"]),
})
export type ReportedCost = typeof ReportedCost.Type

/** A non-token unit some cloud vendors bill in (later). */
export const VendorUnits = Schema.Struct({
  unit: Schema.String.check(Schema.isMaxLength(32)), // "acu" | "ai_credit" | "codex_credit" | "session_hour" …
  amount: Schema.Number.check(Schema.isGreaterThanOrEqualTo(0)),
})
export type VendorUnits = typeof VendorUnits.Type

/**
 * A plan as the CLI names it: provider + its raw tier ("max", "pro", "plus", "team" …). Display via core `planLabel`;
 * whether the person or an organisation pays the seat via core `planSeat` (derived from the tier, never stored).
 */
export const PlanRef = Schema.Struct({
  provider: UsageProvider,
  tier: Schema.String.check(Schema.isMaxLength(40)),
})
export type PlanRef = typeof PlanRef.Type

/**
 * Whose plan or key paid. Set at RunEnded. Never derived from `Run.ownerId` (that is "who may execute"). `Run.fundedBy`
 * is the run's default; a Usage or ComputeUsage row may carry its own `fundedBy` override (later: a platform sandbox
 * whose compute the workspace pays while a person's key pays the tokens). No row override is produced in wave F.
 */
export const FundedBy = Schema.Struct({
  kind: Schema.Literals(["plan", "key", "workspace", "platform", "unknown"]),
  /**
   * plan/key: the person whose plan seat or key it is (the runner's owner today; a personal key's owner later). For an
   * org seat (core `planSeat(plan) === "org"`) this is the seat holder, not the payer.
   */
  ownerId: Schema.NullOr(UserId),
  runnerId: Schema.NullOr(RunnerId),
  profileId: Schema.NullOr(ProfileId),
  /** A model key's id when one is known (workspace key; later personal keys). Opaque; never a secret. */
  credentialId: Schema.NullOr(Schema.String.check(Schema.isMaxLength(64))),
  plan: Schema.NullOr(PlanRef),
  /** How Minga knows: the CLI at run time, the profile probe, the workspace key, the operator env key, an old runner. */
  source: Schema.Literals(["cli", "probe", "workspace_key", "operator_key", "legacy"]),
  /**
   * Decision 0128: the subscription's scope when it was set explicitly ("paid by Jannik's Claude Max (team)"): `team`
   * runs anyone's work, `personal` only its owner's, `projects` (decision 0166 §3.1) anyone's in some projects. Absent =
   * not known (older rows, a key).
   */
  scope: Schema.optionalKey(Schema.Literals(["personal", "team", "projects"])),
  /**
   * Decision 0167 §5: the account the run's profile ran on (its opaque `quotaAccountKey`), set at RunEnded from the
   * hub's runner record. Never shown raw. Absent on older rows and on profiles that never probed their account.
   */
  accountKey: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(128))),
  /** Decision 0167 §2: the run reported it ran on extra usage (Final `funding.extraUsage`). */
  extraUsage: Schema.optionalKey(Schema.Boolean),
})
export type FundedBy = typeof FundedBy.Type

/** What a runner says about how a run was paid (hub Final). The hub adds the owner from its RunnerRecord. */
export const RunFunding = Schema.Struct({
  auth: Schema.Literals(["subscription", "api_key", "unknown"]),
  /** e.g. "apiKeySource:none", "apiKeySource:/login managed key", "account:chatgpt", "account:amazonBedrock". */
  detail: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(80))),
  plan: Schema.optionalKey(PlanRef),
  /**
   * A state flag observed while this run was running, never a balance delta (balances are account-wide): Claude, a
   * rate_limit_event with isUsingOverage; Codex, a rate-limit snapshot with rateLimitReachedType set, or a window
   * exhausted while credits.hasCredits.
   */
  extraUsage: Schema.optionalKey(Schema.Boolean),
})
export type RunFunding = typeof RunFunding.Type

/** Sandbox compute of one run (Platform Runner task sandboxes, decision 0107). Always an upper bound (CPU at 100%). */
export const ComputeUsage = Schema.Struct({
  provider: Schema.String.check(Schema.isMaxLength(40)), // "cloudflare" first; not a closed literal
  billingClass: Schema.Literal("compute"),
  /** Overrides Run.fundedBy for this row (see FundedBy). */
  fundedBy: Schema.optionalKey(FundedBy),
  size: Schema.Literals(["small", "medium", "large", "custom"]),
  instanceType: Schema.String.check(Schema.isMaxLength(40)), // "standard-2"
  vcpu: Schema.Number,
  memoryMib: NonNegInt,
  diskMb: NonNegInt,
  runningSeconds: NonNegInt,
  /** Upper-bound rate (CPU counted at 100%), micro-USD per running hour, from core/pricing.ts CONTAINER_PRICES (decision 0107). */
  rateMicroUsdPerHour: NonNegInt,
  listMicroUsd: NonNegInt,
  /** Money actually charged for this compute (the two-number rule holds for compute too); null = unknown. */
  costMicroUsd: Schema.NullOr(NonNegInt),
  costSource: CostSource,
  upperBound: Schema.Boolean,
  priceTableVersion: Schema.String,
  /**
   * Decision 0107: the Cloudflare container instance (its Durable Object id, hex) and the run's measured window (epoch
   * ms, first start to last stop), so the row can be reconciled with Cloudflare's per-instance, per-minute
   * `containersUsageAdaptiveGroups` later. Absent on rows from before 0107.
   */
  instanceId: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(80))),
  window: Schema.optionalKey(Schema.Struct({ from: NonNegInt, to: NonNegInt })),
})
export type ComputeUsage = typeof ComputeUsage.Type

/**
 * One model's cumulative totals in a Claude session (`result.modelUsage[m]`), kept on `SessionRef.claude.usageTotals`
 * so the next run on the session can take its per-run delta (resumed sessions report the whole session).
 */
export const ModelTotals = Schema.Struct({
  model: Schema.String,
  inputTokens: NonNegInt,
  outputTokens: NonNegInt,
  cacheReadTokens: NonNegInt,
  cacheWriteTokens: NonNegInt,
  costMicroUsd: NonNegInt,
})
export type ModelTotals = typeof ModelTotals.Type

/** A UTC day, "YYYY-MM-DD". Usage days are UTC in v1. */
export const DayString = Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/))
export type DayString = typeof DayString.Type
// #endregion usage-v2
